import { Images } from '@/assets';
import { fonts } from '@/theme/fonts';
import styled from 'styled-components';
import OrderFileList from './OrderFileList';
import { useCallback, useRef } from 'react';
import { useLoading } from '@/context/LoadingContext';
import { deletePartnerConfirmFile } from '@/api/order-detail/deletePartnerConfirmFile';
import { useDispatch } from 'react-redux';
import { order } from '@/stores/slices/order/orderSlice';
import {
  FileTransferService,
  FileTransferSource,
  FileUploadSource,
} from '@/libs/r2-service-client';
import { postPartnerConfirmFile } from '@/api/order-detail/postPartnerConfirmFile';
import { formatAttachedFile } from '@/utils/file/formatAttachedFile';
import { GetOrderDetailResponse } from '@/api/type/types';
import { useAlert } from '@/hooks/useAlert';
import { ATTACHED_FILE_TYPE, ORDER_STATUS_TYPE } from '@/api/type/commonTypes';
import { useTranslation } from 'react-i18next';

const allowExtensions = [
  'stl',
  'proj',
  'opack',
  'fpack',
  'mpack',
  'ppack',
  'zip',
  'jpg',
  'png',
  'bmp',
  'nc',
  'txt',
  'pdf',
];

const MAX_FILE_COUNT = 10;
const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500MB
interface OrderFilesProps {
  orderId: string;
  orderDetail: GetOrderDetailResponse;
  orderStatus: ORDER_STATUS_TYPE;
}

const OrderFiles = ({ orderId, orderDetail, orderStatus }: OrderFilesProps) => {
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const onClickDelete = useCallback(
    (fileId: string) => async () => {
      startLoading();
      try {
        const res = await deletePartnerConfirmFile({
          orderId: orderId,
          confirmationFileId: fileId,
        });
        if (res.isSuccess) {
          dispatch(
            order.deletePartnerConfirmFile({
              fileId: fileId,
              orderId: orderId,
            })
          );
        } else {
          console.log('error', res.error.code);
        }
      } finally {
        stopLoading();
      }
    },
    [dispatch, orderId, startLoading, stopLoading]
  );

  const onClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** 컨펌 파일 업로드 API 통신 */
  const uploadPartnerConfirmApi = useCallback(
    async (attachFiles: ATTACHED_FILE_TYPE[]) => {
      const res = await postPartnerConfirmFile({
        orderId: orderId,
        confirmFiles: attachFiles,
      });
      if (res.isSuccess) {
        if (res.data) {
          dispatch(
            order.addPartnerConfirmFiles({
              files: res.data,
              orderId: orderId,
            })
          );
        }
      }
    },
    [dispatch, orderId]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      startLoading();

      // 파일 개수 체크
      if (files.length > MAX_FILE_COUNT) {
        showAlert({ message: t('youCanUploadUpTo10Images') });
        stopLoading();
        return;
      }
      console.log('files', files);
      try {
        // 확장자 체크
        const invalidFiles = files.filter((file) => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return !ext || !allowExtensions.includes(ext);
        });
        if (invalidFiles.length) {
          showAlert({
            message: t('unsupportedFileFormat'),
          });
          stopLoading();
          return;
        }
        // 용량 체크
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          showAlert({
            message: t('fileUploadLimitfileSizeMustBeMbOrLess', {
              size: '500MB',
            }),
          });
          return;
        }
        const uploadSource = new FileUploadSource('USER_UPLOAD');
        console.log('uploadSource', uploadSource);
        files.forEach((file) => uploadSource.addBrowserData(file));

        const transferSource = new FileTransferSource(uploadSource);
        const fileService = new FileTransferService();

        const uploadResult = await fileService.transfer(transferSource);
        console.log('uploadResult', uploadResult);
        if (!uploadResult.upload) return;
        if (fileInputRef.current) fileInputRef.current.value = '';
        console.log('uploadResult.upload', uploadResult.upload);
        const attachFiles = formatAttachedFile(uploadResult.upload);
        console.log('uploadResult.upload', uploadResult.upload);
        uploadPartnerConfirmApi(attachFiles);
      } finally {
        stopLoading();
      }
    },
    [showAlert, startLoading, stopLoading, t, uploadPartnerConfirmApi]
  );

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0)
        if (e.target.files) {
          uploadFiles(Array.from(e.target.files));
        }
    },
    [uploadFiles]
  );

  const onClickDownload = useCallback(
    (fileId: string, fileName?: string) => async () => {
      console.log('fileName', fileName);
      try {
        const response = await fetch(
          FileTransferService.getDownloadUri(fileId)
        );

        if (!response.ok) {
          throw new Error('파일 다운로드 실패');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName ?? '';
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('다운로드 실패', error);
      }
    },
    []
  );

  return (
    <Container>
      <Section>
        <TitleWrap>
          <IconWrap>
            <Img src={Images.DesignFileIcon} alt="design-file-icon" />
            <Title>{t('designDiagnosisFile')}</Title>
          </IconWrap>
          {orderStatus === 'IN_PROGRESS' && (
            <UploadButton onClick={onClickUpload} />
          )}
          <FileInput
            type="file"
            ref={fileInputRef}
            onChange={onFileChange}
            multiple
          />
        </TitleWrap>
        <OrderFileList
          fileList={orderDetail.partnerConfirmationFiles}
          uploadedBy="PARTNER"
          onClickIcon={onClickDelete}
          status={orderDetail.status}
        />
      </Section>
      <Section>
        <TitleWrap>
          <IconWrap>
            <Img src={Images.ConfirmFileIcon} alt="confirm-file-icon" />
            <Title>{t('customerConfirmationFile')}</Title>
          </IconWrap>
        </TitleWrap>
        <OrderFileList
          fileList={orderDetail.playerConfirmationFiles}
          uploadedBy="PLAYER"
          onClickIcon={onClickDownload}
          status={orderDetail.status}
        />
        <InfoWrap>
          <div>
            💡 {t('fileNameGuide')}
            <br />· _UR: {t('playerRevisionUploaded')}
            <br />· _UC / _C: {t('finalConfirmedFilesByThePlayer')}
          </div>
        </InfoWrap>
      </Section>
    </Container>
  );
};

export default OrderFiles;

const Container = styled.div`
  display: flex;
  gap: 18px;
  overflow: hidden;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 300px;
  /* flex: 1; */
`;

const TitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  padding-right: 5px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: end;
  gap: 5px;
`;

const Img = styled.img`
  width: 20px;
  height: 20px;
`;

const Title = styled.div`
  font-size: 14px;
  line-height: 120%;
  color: #769bf7;
`;

const UploadButton = styled.button`
  background-image: url(${Images.FileUploadIcon});
  width: 20px;
  height: 20px;
`;

const FileInput = styled.input`
  display: none;
`;

const InfoWrap = styled.div`
  font-family: ${fonts.family.inter};
  font-size: 10px;
  font-weight: ${fonts.weight.bold};
  color: #ff0000;
  text-align: left;
`;
